import type { ChannelDto, ForumTagDto } from '@lagerfeuer/shared';
import { EmojiText } from '../../components/EmojiText';
import { Archive, Lock, MessageSquare, MessagesSquare, Paperclip, Plus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { errorMessage } from '../../lib/api';
import { useAppStore } from '../../store/app';
import { can, displayName, useChannelPermissions } from '../../store/selectors';
import { MarkdownInline } from '../messages/Markdown';
import {
  absoluteTime,
  lastActivity,
  relativeTime,
  replyCount,
  useLiveThread,
  useStarterMessage,
  useThreadList,
  type ThreadSort,
  type ThreadWithFirst,
} from '../threads/threadUtils';
import { NewPostDialog } from './NewPostDialog';
import { TagChip } from './TagPicker';
import './forum.css';

/** Forenkanal: Beitragsliste mit Sortierung, Tag-Filter, Suche und „Neuer Beitrag". Beiträge öffnen im Seitenpanel. */
export function ForumView({ channelId }: { channelId: string }) {
  const forum = useAppStore((s) => s.channels[channelId]);
  const [params, setParams] = useSearchParams();
  const openId = params.get('thread');
  const perms = useChannelPermissions(channelId);
  const canPost = can(perms, 'CREATE_THREADS', 'SEND_IN_THREADS');
  const [sort, setSort] = useState<ThreadSort>('activity');
  const [tagId, setTagId] = useState('');
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [composer, setComposer] = useState(false);
  const list = useThreadList(channelId, { archived, tagId, sort });
  const needle = search.trim().toLowerCase();
  const posts = (list.data?.pages.flatMap((p) => p.threads) ?? []).filter((t) => !needle || (t.name ?? '').toLowerCase().includes(needle));
  const tags = [...(forum?.availableTags ?? [])].sort((a, b) => a.position - b.position);

  const open = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('thread', id);
    next.delete('message');
    setParams(next);
  };

  if (!forum) return null;
  return (
    <div className="forum-view" data-testid="forum-view" data-channel-id={channelId}>
      <div className="forum-scroll scroll">
        <div className="forum-inner">
          <div className="forum-toolbar" role="toolbar" aria-label="Forum-Werkzeuge">
            <input
              className="input forum-search"
              type="search"
              placeholder="Beiträge durchsuchen"
              aria-label="Beiträge durchsuchen"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="forum-search"
            />
            <label className="sr-only" htmlFor={`forum-sort-${channelId}`}>
              Sortierung
            </label>
            <select id={`forum-sort-${channelId}`} className="select" value={sort} onChange={(e) => setSort(e.target.value as ThreadSort)} data-testid="forum-sort">
              <option value="activity">Letzte Aktivität</option>
              <option value="created">Neueste zuerst</option>
            </select>
            <button className="btn" aria-pressed={archived} onClick={() => setArchived(!archived)} data-testid="forum-archived-toggle">
              <Archive size={16} aria-hidden="true" /> {archived ? 'Aktive zeigen' : 'Archiv'}
            </button>
            {canPost && (
              <button className="btn btn-primary" onClick={() => setComposer(true)} data-testid="forum-new-post">
                <Plus size={16} aria-hidden="true" /> Neuer Beitrag
              </button>
            )}
          </div>

          {forum.topic && (
            <details className="forum-guidelines" data-testid="forum-guidelines">
              <summary>Richtlinien</summary>
              <MarkdownInline content={forum.topic} serverId={forum.serverId} block />
            </details>
          )}

          {tags.length > 0 && <TagFilter tags={tags} value={tagId} onChange={setTagId} />}

          {list.isLoading && (
            <div className="empty">
              <div className="spinner" />
            </div>
          )}
          {list.isError && (
            <p className="error-text" role="alert">
              Beiträge konnten nicht geladen werden: {errorMessage(list.error)}
            </p>
          )}
          {list.isSuccess && posts.length === 0 && (
            <div className="empty" data-testid="forum-empty">
              <MessagesSquare size={36} aria-hidden="true" />
              <p>{archived ? 'Keine archivierten Beiträge.' : needle || tagId ? 'Keine passenden Beiträge.' : 'Noch keine Beiträge – fang an!'}</p>
            </div>
          )}

          <ul className="forum-list" role="list" aria-label="Beiträge" data-testid="forum-list">
            {posts.map((t) => (
              <li key={t.id}>
                <PostCard post={t} forum={forum} active={openId === t.id} onOpen={() => open(t.id)} />
              </li>
            ))}
          </ul>
          {list.hasNextPage && (
            <button className="btn btn-ghost btn-block" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} data-testid="forum-more">
              Weitere Beiträge laden
            </button>
          )}
        </div>
      </div>
      {composer && (
        <NewPostDialog
          forum={forum}
          onClose={() => setComposer(false)}
          onCreated={(t) => {
            setComposer(false);
            open(t.id);
          }}
        />
      )}
    </div>
  );
}

function TagFilter({ tags, value, onChange }: { tags: ForumTagDto[]; value: string; onChange: (id: string) => void }) {
  return (
    <div className="forum-tag-filter" role="group" aria-label="Nach Tag filtern" data-testid="forum-tag-filter">
      <button className="forum-filter-chip" aria-pressed={value === ''} onClick={() => onChange('')} data-testid="forum-tag-chip" data-tag-id="">
        Alle
      </button>
      {tags.map((t) => (
        <button
          key={t.id}
          className="forum-filter-chip"
          aria-pressed={value === t.id}
          onClick={() => onChange(value === t.id ? '' : t.id)}
          data-testid="forum-tag-chip"
          data-tag-id={t.id}
        >
          {t.emoji && <EmojiText value={t.emoji} size={16} />}
          {t.name}
        </button>
      ))}
    </div>
  );
}

function PostCard({ post, forum, active, onOpen }: { post: ThreadWithFirst; forum: ChannelDto; active: boolean; onOpen: () => void }) {
  const t = useLiveThread(post);
  const starter = useStarterMessage(post.id, post.firstMessage ?? null);
  const owner = useAppStore((s) => (t.ownerId ? s.users[t.ownerId] : undefined));
  const msg = starter.data;
  const author = msg?.author ?? owner ?? null;
  const appliedTags = (t.thread?.appliedTagIds ?? []).map((id) => forum.availableTags?.find((x) => x.id === id)).filter((x) => !!x);
  const replies = replyCount(t, true);
  const ts = lastActivity(t);
  const titleId = `forum-post-${t.id}-title`;
  return (
    <article
      className="forum-card"
      role="button"
      tabIndex={0}
      aria-labelledby={titleId}
      aria-current={active ? 'true' : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      data-testid="forum-post"
      data-thread-id={t.id}
    >
      <h3 className="forum-card-title" id={titleId} data-testid="forum-post-title">
        {t.thread?.locked && <Lock size={14} aria-label="gesperrt" role="img" />}
        {t.thread?.archived && <Archive size={14} aria-label="archiviert" role="img" />}
        <span className="truncate">{t.name}</span>
      </h3>
      {appliedTags.length > 0 && (
        <div className="forum-tags" aria-label="Tags">
          {appliedTags.map((tag) => (
            <TagChip key={tag.id} tag={tag} small />
          ))}
        </div>
      )}
      {msg && !msg.deleted && msg.content && (
        <div className="forum-card-preview" data-testid="forum-post-preview">
          <MarkdownInline content={msg.content.slice(0, 400)} serverId={t.serverId} />
        </div>
      )}
      <div className="forum-card-meta">
        <span>
          <Avatar user={author} size={18} />
          <span className="forum-author" data-testid="forum-post-author">
            {author ? displayName(author) : 'Unbekannt'}
          </span>
        </span>
        <span data-testid="forum-post-replies" aria-label={`${replies} Antworten`}>
          <MessageSquare size={14} aria-hidden="true" />
          {replies}
        </span>
        {msg && msg.attachments.length > 0 && (
          <span aria-label={`${msg.attachments.length} Anhänge`}>
            <Paperclip size={14} aria-hidden="true" />
            {msg.attachments.length}
          </span>
        )}
        <span>
          <time dateTime={new Date(ts).toISOString()} title={absoluteTime(ts)} data-testid="forum-post-activity">
            {relativeTime(ts)}
          </time>
        </span>
      </div>
    </article>
  );
}
